// src/components/account/ReviewCard.tsx

import React from 'react';

interface ReviewCardProps {
  review: any; // Replace with Review type
  onApprove: (reviewId: number) => void;
  onRemove: (reviewId: number) => void;
  onRespond: (review: any) => void;
  onLike: (reviewId: number) => void;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ 
  review, 
  onApprove, 
  onRemove, 
  onRespond, 
  onLike 
}) => {
  return (
    <div key={review.id} className="border border-gray-200 rounded-xl p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <p className="text-sm font-medium text-gray-500">{review.listingName}</p>
            <span className={`px-3 py-1 text-xs font-medium rounded-full ${
              review.status === 'approved' 
                ? 'bg-green-100 text-green-700' 
                : 'bg-yellow-100 text-yellow-700'
            }`}>
              {review.status === 'approved' ? 'Approved' : 'Pending'}
            </span>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <h4 className="font-semibold text-gray-900">{review.author}</h4>
            <div className="flex items-center">
              {[...Array(5)].map((_, i) => (
                <i
                  key={i}
                  className={`ri-star-fill text-sm ${
                    i < review.rating ? 'text-yellow-400' : 'text-gray-300'
                  }`}
                ></i>
              ))}
            </div>
          </div>
          <span className="text-sm text-gray-500">{review.date}</span>
        </div>
      </div>

      <p className="text-gray-700 mb-4">{review.comment}</p>

      <div className="mb-4">
        <button 
          onClick={() => onLike(review.id)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors cursor-pointer ${
            review.isLiked 
              ? 'bg-teal-50 text-teal-700' 
              : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
          }`}
        >
          <i className={`${review.isLiked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} text-lg`}></i>
          <span className="text-sm font-medium">
            {review.isLiked ? 'Liked' : 'Helpful'} ({review.helpful})
          </span>
        </button>
      </div>

      {review.response ? (
        <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-teal-500 mb-4">
          <p className="text-sm font-medium text-gray-900 mb-1">Your Response:</p>
          <p className="text-sm text-gray-700">{review.response}</p>
        </div>
      ) : null}

      <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
        {review.status === 'pending' && (
          <button 
            onClick={() => onApprove(review.id)}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-check-line mr-2"></i>
            Approve
          </button>
        )}
        <button 
          onClick={() => onRespond(review)}
          className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-reply-line mr-2"></i>
          {review.response ? 'Edit Response' : 'Respond'}
        </button>
        <button 
          onClick={() => onRemove(review.id)}
          className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-delete-bin-line mr-2"></i>
          Remove
        </button>
      </div>
    </div>
  );
};

export default ReviewCard;